import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { db } from '../firebase/firebaseConfig';
import { ref, query, orderByChild, equalTo, get } from 'firebase/database';
import StatsCard from '../components/StatsCard';
import { getLatestVersion } from '../data/changelogData';
import './EmbedWidget.css';

function EmbedWidget() {
  const { profileId } = useParams();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Extract theme from URL query (?theme=light|dark)
  const [theme, setTheme] = useState('dark');

  useEffect(() => {
    // Disable external scrolling for embeds
    document.body.style.backgroundColor = "transparent";
    document.body.style.overflow = "hidden";

    const fetchProfile = async () => {
      try {
        // 1. Check Maintenance Mode first
        const settingsSnap = await get(ref(db, 'settings/maintenanceMode'));
        if (settingsSnap.exists() && settingsSnap.val() === true) {
          setError("Platform Maintenance");
          setLoading(false);
          return;
        }

        // 2. Fetch User Profile
        const usersRef = ref(db, 'users');
        const q = query(usersRef, orderByChild('profileId'), equalTo(profileId));
        const snapshot = await get(q);

        if (snapshot.exists()) {
          const data = snapshot.val();
          const firstKey = Object.keys(data)[0];
          const userData = data[firstKey];
          
          if (userData.settings?.profileVisibility === 'private') {
            setError("Profile Private");
            setLoading(false);
            return;
          }

          if (userData.status === 'suspended' || userData.status === 'banned' || userData.status === 'blocked') {
            setError("Access Restricted");
            setLoading(false);
            return;
          }
          
          setProfile(userData);
        }
        
        // 3. Set Theme based on URL param, User setting, or Admin default
        const searchParams = new URLSearchParams(location.search);
        const urlTheme = searchParams.get('theme');
        if (urlTheme) {
          setTheme(urlTheme);
        } else {
           const adminSettingsSnap = await get(ref(db, 'settings/defaultEmbedTheme'));
           if (adminSettingsSnap.exists()) {
             setTheme(adminSettingsSnap.val());
           }
        }
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };

    fetchProfile();
    
    return () => {
      document.body.style.backgroundColor = "";
      document.body.style.overflow = "";
    };
  }, [profileId]);

  if (loading) return <div className={`embed-loader ${theme === 'light' ? 'theme-light' : ''}`}><div className="loader"></div></div>;
  if (error) return <div className={`embed-error ${theme === 'light' ? 'theme-light' : ''}`}>{error}</div>;
  if (!profile) return <div className={`embed-error ${theme === 'light' ? 'theme-light' : ''}`}>Profile Not Found</div>;

  return (
    <div className={`embed-container ${theme === 'light' ? 'theme-light' : ''}`}>
      <div className="embed-header glass-card">
        {profile.avatar ? (
          <img src={profile.avatar} alt="Avatar" className="embed-avatar" />
        ) : (
          <div className="embed-avatar-placeholder"><i className="fa-solid fa-user"></i></div>
        )}
        <div className="embed-user-info">
          <h2>{profile.name}</h2>
          <a href={`https://devtracker.app/dev/${profile.profileId}`} target="_blank" rel="noreferrer" className="embed-link">
            Powered by DevTracker
          </a>
        </div>
      </div>

      <div className="embed-scroll-area">
        <div className="embed-stats-list">
          <StatsCard platform="GitHub" username={profile.githubUsername} accentColor="var(--brand-github)" iconClass="fa-brands fa-github" />
          <StatsCard platform="LeetCode" username={profile.leetcodeUsername} accentColor="var(--brand-leetcode)" iconClass="fa-solid fa-code" />
          <StatsCard platform="Codeforces" username={profile.codeforcesUsername} accentColor="var(--brand-codeforces)" iconClass="fa-solid fa-chart-simple" />
        </div>
      </div>
      
      <div className="embed-footer" style={{textAlign: 'center', padding: '10px', fontSize: '0.75rem', color: 'var(--text-secondary)', borderTop: '1px solid rgba(255,255,255,0.05)', marginTop: 'auto'}}>
        &copy; {new Date().getFullYear()} DevTracker &bull; {getLatestVersion()}
      </div>
    </div>
  );
}

export default EmbedWidget;
